import { useMemo, useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import { authLabels } from "@/i18n";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";

const NEWS_IMAGES = [
  "/assets/auth_a.jpg",
  "/assets/auth_b.jpg",
];

type MobileNewsSectionProps = {
  /** Tighter layout for Capacitor /auth screen */
  compact?: boolean;
};

const MobileNewsSection = ({ compact = false }: MobileNewsSectionProps) => {
  const { language } = useLanguage();
  const labels = useMemo(() => authLabels(language), [language]);
  const newsItems = useMemo(
    () =>
      (labels.news?.items || []).map((item: { title: string; text: string }, i: number) => ({
        ...item,
        image: NEWS_IMAGES[i % NEWS_IMAGES.length],
      })),
    [labels.news?.items]
  );
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [currentNews, setCurrentNews] = useState(0);
  const { theme } = useTheme();
  const isDark = theme === "dark";

  useEffect(() => {
    if (!carouselApi) return;
    const onSelect = () => setCurrentNews(carouselApi.selectedScrollSnap());
    onSelect();
    carouselApi.on("select", onSelect);
    return () => {
      carouselApi.off("select", onSelect);
    };
  }, [carouselApi]);

  const hasNews = newsItems.length > 0;
  const swipeable = hasNews && newsItems.length > 1;

  return (
    <Card
      className={`w-full relative overflow-hidden border-0 shadow-none ${
        compact ? "h-[12.5rem] rounded-xl" : "mx-4 h-[220px]"
      } ${
        isDark
          ? "bg-black/50 backdrop-blur-sm"
          : "bg-white/95 backdrop-blur-sm"
      }`}
    >
      <div
        className={`absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 via-black/40 to-transparent pointer-events-none ${
          compact ? "px-3 pt-3 pb-8" : "px-4 pt-4 pb-10"
        }`}
      >
        <h1
          className={`font-bold text-white leading-tight ${
            compact
              ? "text-base sm:text-lg whitespace-nowrap truncate"
              : "text-lg sm:text-xl"
          }`}
        >
          {labels.news?.welcomeTitleSingle ||
            `${labels.news?.welcomeTitleLine1 || "Välkommen till"} ${labels.news?.welcomeTitleLine2 || "BILENIA Auktioner"}`}
        </h1>
        <p
          className={`text-gray-200 mt-1 line-clamp-2 max-w-[95%] ${
            compact ? "text-[11px]" : "text-xs"
          }`}
        >
          {labels.news?.welcomeText ||
            "Exklusiva auktioner och direktköpsannonser – hitta ditt nästa fordon hos BILENIA."}
        </p>
      </div>
      {hasNews ? (
        <Carousel
          setApi={setCarouselApi}
          opts={{ align: "start", loop: swipeable, dragFree: false }}
          className="h-full w-full [&_.overflow-hidden]:!h-full"
        >
          <CarouselContent className="h-full ml-0">
            {newsItems.map((item, index) => (
              <CarouselItem key={index} className="pl-0 basis-full h-full">
                <div className="relative h-full w-full">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover opacity-70"
                    draggable={false}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent flex flex-col justify-end p-3">
                    <h3 className="text-base font-bold text-white mb-0.5 line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="text-[11px] text-gray-300 line-clamp-2">{item.text}</p>
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      ) : (
        <img
          src={NEWS_IMAGES[0]}
          alt=""
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover opacity-50"
        />
      )}

      {swipeable && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
          {newsItems.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === currentNews ? "w-4 bg-white" : "w-1.5 bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </Card>
  );
};

export default MobileNewsSection;
